import {
  Box,
  Grid,
  TextField,
  Typography,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  InputLabel,
  Select,
} from "@mui/material";
import EmailInputField from "../../../../components/EmailInputField/EmailInputField";
import { normalizeEmails, isValidEmailList } from "../../../../utils/emailValidation";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import DeleteIcon from "@mui/icons-material/Delete";

// ── Static constants ──────────────────────────────────────────────────────────

const REQUIRED_DOC_OPTIONS = [
  { id: 106, name: "Prenote" },
  { id: 100, name: "BOL" },
  { id: 108, name: "TIR" },
];

const DELIVERY_METHODS = [
  { code: "EMAIL", name: "Email", id: 1 },
  { code: "EMAIL_INV_ONLY", name: "Email - Invoice Only", id: 2 },
  { code: "EDI", name: "EDI", id: 3 },
  { code: "WEB", name: "Web", id: 4 },
];

const POPUP_METHODS = {
  EDI: "EDI requires additional setup. Please contact your EDI coordinator to configure the connection.",
};

const EMPTY_FORM_VALUES = {
  invoiceDeliveryMethod: "",
  invoiceValue: "",
  requiredDocuments: [],
};

// ── Pure helpers ──────────────────────────────────────────────────────────────

const getDefaultCheckedDocs = (methodCode) =>
  methodCode === "EDI" ? [106, 100] : [106, 100, 108];

const getInfoText = (methodCode) => {
  if (methodCode === "EMAIL") return "Checked documents will be emailed with Invoice";
  if (methodCode === "EDI") return "EDI only, No documents will be sent in EDI";
  if (methodCode === "EMAIL_INV_ONLY") return "No documents will be emailed with Invoice";
  return "";
};

// ── Validation schema ─────────────────────────────────────────────────────────
//
// Rules:
//  • invoiceValue — required + format check for EMAIL / EMAIL_INV_ONLY / WEB
//  • requiredDocuments — at least one must be checked when a delivery method
//    is selected (enforced regardless of method type)

const getValidationSchema = (methodCode) => {
  // Base: always require at least one checked document when a method is chosen
  const baseShape = {
    requiredDocuments: Yup.array().when("invoiceDeliveryMethod", {
      is: (val) => Boolean(val),
      then: (schema) =>
        schema.min(1, "At least one required document must be selected"),
      otherwise: (schema) => schema,
    }),
  };

  if (methodCode === "EMAIL" || methodCode === "EMAIL_INV_ONLY") {
    return Yup.object({
      ...baseShape,
      invoiceValue: Yup.string()
        .required("Email is required")
        .test(
          "valid-emails",
          "Invalid email format. Use comma or semicolon.",
          (value) => isValidEmailList(value),
        ),
    });
  }

  if (methodCode === "WEB") {
    return Yup.object({
      ...baseShape,
      invoiceValue: Yup.string()
        .required("Website is required")
        .test("valid-url", "Enter a valid website URL", (value) => {
          if (!value) return false;
          return /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/.test(
            value.trim(),
          );
        }),
    });
  }

  // EDI — no invoiceValue validation, but still require at least one doc
  return Yup.object(baseShape);
};

// ── Derive form values from initialData (single source of truth) ──────────────

const deriveFormValues = (initialData) => {
  if (!initialData) return EMPTY_FORM_VALUES;
  return {
    invoiceDeliveryMethod: initialData.deliveryMethod || "",
    invoiceValue: (initialData.addresses || []).join(", "),
    requiredDocuments: (initialData.requiredDocumentCodes || []).map(Number),
  };
};

// ── Component ─────────────────────────────────────────────────────────────────

const InvoiceRequirementsSection = forwardRef(function InvoiceRequirementsSection(
  { isEditMode, setInvoiceValues, onDirtyChange, initialData, onDelete },
  ref,
) {
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [methodChangeOpen, setMethodChangeOpen] = useState(false);
  const [pendingMethodCode, setPendingMethodCode] = useState(null);

  const snapshotRef = useRef(null);
  const lastDirtyRef = useRef(false);

  // ── Formik ────────────────────────────────────────────────────────────────

  const formik = useFormik({
    initialValues: EMPTY_FORM_VALUES,
    validate: (vals) => {
      try {
        getValidationSchema(vals.invoiceDeliveryMethod).validateSync(vals, {
          abortEarly: false,
        });
        return {};
      } catch (err) {
        const errors = {};
        err.inner.forEach((e) => {
          errors[e.path] = e.message;
        });
        return errors;
      }
    },
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  // ── Imperative handle ─────────────────────────────────────────────────────
  //
  // validateForSave — called by BillToFormContainer before dispatching the
  // invoice save API. Touches all fields so errors surface in the UI and
  // returns true only when the form is valid.

  useImperativeHandle(ref, () => ({
    cancelEdit: () => {
      if (snapshotRef.current) {
        formik.resetForm({ values: snapshotRef.current });
      }
      snapshotRef.current = null;
      setMethodChangeOpen(false);
      setPendingMethodCode(null);
    },

    validateForSave: async () => {
      // Touch every field so all errors become visible
      formik.setTouched(
        {
          invoiceDeliveryMethod: true,
          invoiceValue: true,
          requiredDocuments: true,
        },
        false, // don't trigger a second re-render via validateOnChange
      );
      const errors = await formik.validateForm();
      return Object.keys(errors).length === 0;
    },
  }));

  // ── Populate form from GET response ───────────────────────────────────────

  const prevInitialDataRef = useRef(undefined);

  useEffect(() => {
    if (prevInitialDataRef.current === initialData) return;
    prevInitialDataRef.current = initialData;

    formik.resetForm({ values: deriveFormValues(initialData) });
    if (isEditMode && initialData !== undefined) {
      snapshotRef.current = deriveFormValues(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // ── Snapshot on edit mode toggle ──────────────────────────────────────────

  useEffect(() => {
    if (isEditMode) {
      snapshotRef.current = deriveFormValues(initialData);
    } else {
      snapshotRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  // ── Notify parent of dirty change ─────────────────────────────────────────

  useEffect(() => {
    if (!onDirtyChange) return;
    if (formik.dirty !== lastDirtyRef.current) {
      lastDirtyRef.current = formik.dirty;
      onDirtyChange(formik.dirty);
    }
  }, [formik.dirty, onDirtyChange]);

  // ── Sync values up to BillToFormContainer ─────────────────────────────────

  useEffect(() => {
    const selectedMethod = DELIVERY_METHODS.find(
      (m) => m.code === formik.values.invoiceDeliveryMethod,
    );
    if (!selectedMethod) return;

    const method = selectedMethod.code;
    const isEmailType = method === "EMAIL" || method === "EMAIL_INV_ONLY";
    const isWeb = method === "WEB";

    setInvoiceValues((prev) => ({
      ...prev,
      invoiceDelivery: {
        deliveryMethod: method,
        requiredDocumentCodes: formik.values.requiredDocuments?.map(Number) || [],
        config: {
          emailAddress: isEmailType ? formik.values.invoiceValue || "" : "",
          websiteUrl: isWeb ? formik.values.invoiceValue || "" : "",
        },
      },
    }));
  }, [
    formik.values.invoiceDeliveryMethod,
    formik.values.invoiceValue,
    formik.values.requiredDocuments,
    setInvoiceValues,
  ]);

  // ── Derived flags ─────────────────────────────────────────────────────────

  const selectedMethod = DELIVERY_METHODS.find(
    (m) => m.code === formik.values.invoiceDeliveryMethod,
  );
  const isEDI = selectedMethod?.code === "EDI";
  const hasMethod = Boolean(formik.values.invoiceDeliveryMethod);

  // ── Method change ─────────────────────────────────────────────────────────

  const handleInvoiceMethodChange = (selectedCode) => {
    const currentMethod = formik.values.invoiceDeliveryMethod;
    if (initialData && currentMethod && selectedCode !== currentMethod) {
      setPendingMethodCode(selectedCode);
      setMethodChangeOpen(true);
      return;
    }
    applyMethodChange(selectedCode);
  };

  const applyMethodChange = (selectedCode) => {
    formik.setValues({
      invoiceDeliveryMethod: selectedCode,
      invoiceValue: "",
      requiredDocuments: getDefaultCheckedDocs(selectedCode),
    });
    formik.setFieldError("invoiceValue", undefined);
    formik.setFieldTouched("invoiceValue", false, false);
    // Clear any stale requiredDocuments error shown from a prior save attempt
    formik.setFieldError("requiredDocuments", undefined);
    formik.setFieldTouched("requiredDocuments", false, false);

    if (POPUP_METHODS[selectedCode]) {
      setPopupMessage(POPUP_METHODS[selectedCode]);
      setPopupOpen(true);
    }
  };

  const handleCheckboxToggle = (docId) => {
    const current = formik.values.requiredDocuments;
    formik.setFieldValue(
      "requiredDocuments",
      current.includes(docId) ? current.filter((d) => d !== docId) : [...current, docId],
    );
    // Clear the checkbox error as soon as the user interacts
    if (formik.errors.requiredDocuments) {
      formik.setFieldError("requiredDocuments", undefined);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Section divider */}
      <Box sx={{ my: 2 }}>
        <Divider textAlign="center">
          <Box
            sx={{
              padding: "5px",
              paddingLeft: "20px",
              paddingRight: "20px",
              border: "1px solid #ccc",
              fontSize: "0.85rem",
            }}
          >
            Invoice Requirements
          </Box>
        </Divider>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Grid container spacing={3}>
          <Grid xs={12} item>
            {/* Delivery Method + Email/URL field */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <InputLabel
                  htmlFor="Delivery Method"
                  style={{ color: "black", fontSize: "0.85rem" }}
                >
                  Delivery Method
                </InputLabel>
                <Select
                  fullWidth
                  size="small"
                  style={{ fontSize: "0.85rem" }}
                  value={formik.values.invoiceDeliveryMethod}
                  onChange={(e) => handleInvoiceMethodChange(e.target.value)}
                  disabled={!isEditMode}
                >
                  {DELIVERY_METHODS.map((option) => (
                    <MenuItem
                      style={{ fontSize: "0.85rem" }}
                      key={option.code}
                      value={option.code}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} sm={8}>
                {selectedMethod?.code === "WEB" ? (
                  <Box>
                    <InputLabel
                      htmlFor="invoiceValue"
                      style={{ color: "black", fontSize: "0.85rem" }}
                    >
                      Website URL
                    </InputLabel>
                    <TextField
                      style={{ fontSize: "0.85rem" }}
                      fullWidth
                      size="small"
                      name="invoiceValue"
                      value={formik.values.invoiceValue}
                      onChange={(e) => {
                        formik.setFieldValue("invoiceValue", e.target.value);
                        if (formik.errors.invoiceValue) {
                          formik.setFieldError("invoiceValue", undefined);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.invoiceValue && Boolean(formik.errors.invoiceValue)
                      }
                      helperText={
                        (formik.touched.invoiceValue && formik.errors.invoiceValue) || " "
                      }
                      FormHelperTextProps={{
                        style: { marginTop: 2, fontSize: "0.72rem" },
                      }}
                      placeholder={
                        !hasMethod
                          ? "Select a delivery method first"
                          : isEDI
                            ? "--Disabled--"
                            : "Enter website URL"
                      }
                      disabled={!isEditMode || !hasMethod || isEDI}
                      sx={
                        !hasMethod || isEDI
                          ? {
                              "& .MuiInputBase-root": { backgroundColor: "#f0f0f0" },
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "#9e9e9e",
                                backgroundColor: "#f0f0f0",
                              },
                            }
                          : {}
                      }
                    />
                  </Box>
                ) : (
                  <Box>
                    <InputLabel
                      style={{ color: "black", fontSize: "0.85rem", display: "block", marginBottom: "4px" }}
                    >
                      Email Address(es)
                    </InputLabel>
                    <EmailInputField
                      value={normalizeEmails(formik.values.invoiceValue)}
                      onChange={(emails) => {
                        formik.setFieldValue("invoiceValue", emails.join(", "));
                        if (formik.errors.invoiceValue) {
                          formik.setFieldError("invoiceValue", undefined);
                        }
                      }}
                      disabled={!isEditMode || !hasMethod || isEDI}
                      multiple={true}
                      label=""
                      placeholder="email1@x.com, email2@y.com"
                      size="small"
                      fullWidth={true}
                    />
                    {formik.touched.invoiceValue && formik.errors.invoiceValue && (
                      <Typography
                        style={{
                          marginTop: "6px",
                          fontSize: "0.72rem",
                          color: "#d32f2f",
                        }}
                      >
                        {formik.errors.invoiceValue}
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Required Documents + Info Text + Delete button */}
            <Grid item xs={12} sx={{ margin: 2 }}>
              <Typography variant="subtitle1" style={{ fontSize: "0.85rem" }}>
                Required Documents
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  {REQUIRED_DOC_OPTIONS.map((doc) => (
                    <FormControlLabel
                      key={doc.id}
                      control={
                        <Checkbox
                          checked={formik.values.requiredDocuments.includes(doc.id)}
                          onChange={() => handleCheckboxToggle(doc.id)}
                          // Disabled when: not in edit mode OR no delivery method selected
                          disabled={!isEditMode || !hasMethod}
                        />
                      }
                      label={
                        <Typography style={{ fontSize: "0.85rem" }}>
                          {doc.name}
                        </Typography>
                      }
                    />
                  ))}
                  {/* At-least-one-checkbox error — shown after a save attempt */}
                  {formik.touched.requiredDocuments &&
                    formik.errors.requiredDocuments && (
                      <Typography
                        style={{
                          fontSize: "0.72rem",
                          color: "#d32f2f",
                          marginTop: "2px",
                        }}
                      >
                        {formik.errors.requiredDocuments}
                      </Typography>
                    )}
                </Box>

                {hasMethod && getInfoText(formik.values.invoiceDeliveryMethod) && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.80rem",
                      color: "#555",
                      fontStyle: "italic",
                      border: "1px dashed #bbb",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    {getInfoText(formik.values.invoiceDeliveryMethod)}
                  </Typography>
                )}

                {/* Delete Invoice button — only when existing config is loaded */}
                {initialData && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon style={{ width: "14px", height: "14px" }} />}
                    style={{
                      fontSize: "0.80rem",
                      marginLeft: "auto",
                      alignSelf: "center",
                    }}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    Delete Invoice
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* EDI setup popup */}
      <Dialog open={popupOpen} onClose={() => setPopupOpen(false)}>
        <DialogTitle>Setup Required</DialogTitle>
        <DialogContent>
          <Typography>{popupMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPopupOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delivery method change warning */}
      <Dialog
        open={methodChangeOpen}
        onClose={() => {
          setMethodChangeOpen(false);
          setPendingMethodCode(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>
          Change Delivery Method
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.90rem" }}>
            Changing the delivery method will clear the existing invoice configuration.
            Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMethodChangeOpen(false);
              setPendingMethodCode(null);
            }}
            variant="outlined"
            size="small"
            style={{ color: "#000" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              applyMethodChange(pendingMethodCode);
              setMethodChangeOpen(false);
              setPendingMethodCode(null);
            }}
            variant="contained"
            size="small"
            style={{ background: "rgb(11, 67, 135)", fontSize: "0.85rem" }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete invoice config confirm */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>
          Delete Invoice Config
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.90rem" }}>
            This will permanently delete the invoice configuration. Are you sure?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            variant="outlined"
            size="small"
            style={{ color: "#000" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              formik.resetForm({ values: EMPTY_FORM_VALUES });
              snapshotRef.current = null;
              if (onDelete) onDelete();
            }}
            variant="contained"
            size="small"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default InvoiceRequirementsSection;
